/**
 * InviteResellerUserDialog (TMMPDM-0015) — email + role (OWNER | ADMIN | STAFF) →
 * `POST /resellers/{id}/users` (202 + the pending invitation). The dialog closes on success
 * AND on any 409 (the panel renders the failure's catalog copy; `RESELLER_CONSOLE_NOT_CONFIGURED`
 * as an info alert). Only a validation failure stays in the dialog: as field errors, or, when it
 * marks no field here, as the form's own line (TMMPDM-0070).
 */
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { inviteResellerUser } from '@/api/saas/resellers';
import { isValidationFailed, validationFields } from '@/api/errors';
import { RESELLER_ROLES, type ResellerRole } from '@/api/saas/types';

interface InviteResellerUserDialogProps {
  open: boolean;
  resellerId: number;
  onClose: () => void;
  onInvited: () => void;
  /** A non-validation failure — the panel renders it. */
  onFailed: (error: unknown) => void;
}

export function InviteResellerUserDialog({
  open,
  resellerId,
  onClose,
  onInvited,
  onFailed,
}: InviteResellerUserDialogProps) {
  const { t } = useCopy();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ResellerRole>('ADMIN');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setEmail('');
    setRole('ADMIN');
    setFieldErrors({});
    setFormError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (email.trim() === '') {
      setFieldErrors({ email: t('resellers.users.emailRequired', 'Email is required.') });
      return;
    }
    setFieldErrors({});
    setFormError(null);
    setSubmitting(true);
    try {
      await inviteResellerUser(resellerId, { email: email.trim(), role });
      reset();
      onInvited();
    } catch (err) {
      if (isValidationFailed(err)) {
        // A refusal that marks no field this form shows still says something: the form's own line.
        const marked = validationFields(err, t);
        if (marked.email !== undefined || marked.role !== undefined) {
          setFieldErrors(marked);
        } else {
          setFormError(t('resellers.users.inviteError', 'Could not send the invitation.'));
        }
      } else {
        reset();
        onFailed(err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('resellers.users.inviteTitle', 'Invite administrator')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError ? <Alert severity="error">{formError}</Alert> : null}
          <TextField
            label={t('resellers.users.emailLabel', 'Email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email}
            size="small"
            fullWidth
          />
          <TextField
            select
            label={t('resellers.users.roleLabel', 'Role')}
            value={role}
            onChange={(e) => setRole(e.target.value as ResellerRole)}
            error={Boolean(fieldErrors.role)}
            helperText={fieldErrors.role}
            size="small"
            fullWidth
          >
            {RESELLER_ROLES.map((r) => (
              <MenuItem key={r} value={r}>
                {t(`resellers.role.${r}`, r)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {t('resellers.cancel', 'Cancel')}
        </Button>
        <SubmitButton onClick={() => void handleSubmit()} busy={submitting}>
          {t('resellers.users.inviteSubmit', 'Send invitation')}
        </SubmitButton>
      </DialogActions>
    </Dialog>
  );
}
